import { useMemo, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { useAlerts, useAlertStats } from '../api/alerts';
import { useEntities } from '../api/entities';
import { useGraphStats } from '../api/graph';
import RiskGauge from '../components/visualization/RiskGauge';
import WidgetGrid from '../components/dashboard/WidgetGrid';
import type { WidgetPlacement } from '../components/dashboard/WidgetGrid';
import type { WidgetSize } from '../components/dashboard/widgets';
import KPIWidget from '../components/dashboard/widgets/KPIWidget';
import ChartWidget from '../components/dashboard/widgets/ChartWidget';
import AlertListWidget from '../components/dashboard/widgets/AlertListWidget';
import MiniGraphWidget from '../components/dashboard/widgets/MiniGraphWidget';
import ActivityFeedWidget from '../components/dashboard/widgets/ActivityFeedWidget';
import RiskOverviewWidget from '../components/dashboard/widgets/RiskOverviewWidget';
import MapWidget from '../components/dashboard/widgets/MapWidget';
import { useWebSocket } from '../hooks/useWebSocket';
import type { AlertSeverity } from '../types';

const SEVERITY_COLORS: Record<AlertSeverity, string> = {
  critical: '#ef4444',
  high: '#f97316',
  medium: '#eab308',
  low: '#3b82f6',
  info: '#06b6d4',
};

const ENTITY_TYPE_COLORS: Record<string, string> = {
  person: '#3b82f6',
  organization: '#a855f7',
  location: '#22c55e',
  ip_address: '#ef4444',
  domain: '#f97316',
  email: '#14b8a6',
  phone_number: '#06b6d4',
  document: '#64748b',
  other: '#94a3b8',
};

// ── Default Layout ──

const DEFAULT_LAYOUT: WidgetPlacement[] = [
  { widgetId: 'kpi-entities', size: '1x1' },
  { widgetId: 'kpi-alerts', size: '1x1' },
  { widgetId: 'kpi-relationships', size: '1x1' },
  { widgetId: 'kpi-communities', size: '1x1' },
  { widgetId: 'chart-activity', size: '2x1' },
  { widgetId: 'chart-entity-distribution', size: '1x1' },
  { widgetId: 'mini-graph', size: '1x1' },
  { widgetId: 'alert-list', size: '2x1' },
  { widgetId: 'chart-severity', size: '1x1' },
  { widgetId: 'risk-gauge', size: '1x1' },
  { widgetId: 'risk-overview', size: '1x1' },
  { widgetId: 'activity-feed', size: '1x2' },
  { widgetId: 'map', size: '1x1' },
];

export default function Dashboard() {
  const navigate = useNavigate();
  const { data: entitiesData } = useEntities({ page: 1, page_size: 1 });
  useAlerts({ page: 1, page_size: 5, sort_by: 'created_at', sort_order: 'desc' });
  const { data: alertStats } = useAlertStats();
  const { data: graphStats } = useGraphStats();

  // ── WebSocket live indicator ──
  const { isConnected, lastMessage } = useWebSocket({ autoConnect: true });
  const [liveEventCount, setLiveEventCount] = useState(0);
  const [lastEventTime, setLastEventTime] = useState<Date | null>(null);

  const handleWsMessage = useCallback(() => {
    if (lastMessage) {
      setLiveEventCount((prev) => prev + 1);
      setLastEventTime(new Date());
    }
  }, [lastMessage]);

  useEffect(() => {
    handleWsMessage();
  }, [handleWsMessage]);

  // KPIs
  const totalEntities = entitiesData?.total ?? 0;
  const activeAlerts = alertStats?.by_status?.new
    ? (alertStats.by_status.new ?? 0) + (alertStats.by_status.in_progress ?? 0)
    : 0;
  const alertsLast24h = alertStats?.last_24h ?? 0;
  const totalRelationships = graphStats?.total_edges ?? 0;

  // Entity type distribution for pie chart
  const entityTypePieData = useMemo(() => {
    if (!graphStats?.entity_type_counts) return [];
    return Object.entries(graphStats.entity_type_counts)
      .filter(([, count]) => count > 0)
      .map(([type, count]) => ({
        name: type.replace('_', ' '),
        value: count,
        color: ENTITY_TYPE_COLORS[type] || ENTITY_TYPE_COLORS.other,
      }));
  }, [graphStats]);

  // Alert severity for bar chart
  const alertSeverityData = useMemo(() => {
    if (!alertStats?.by_severity) return [];
    return Object.entries(alertStats.by_severity).map(([severity, count]) => ({
      severity: severity.charAt(0).toUpperCase() + severity.slice(1),
      count,
      color: SEVERITY_COLORS[severity as AlertSeverity] || '#64748b',
    }));
  }, [alertStats]);

  // Mock activity data for area chart
  const activityData = useMemo(() => {
    return Array.from({ length: 24 }, (_, i) => ({
      hour: `${(23 - i).toString().padStart(2, '0')}:00`,
      entities: Math.floor(Math.random() * 20 + 5),
      alerts: Math.floor(Math.random() * 8),
      analysis: Math.floor(Math.random() * 12 + 2),
    })).reverse();
  }, []);

  // ── Widget Renderer ──

  const renderWidget = (widgetId: string, _size: WidgetSize) => {
    switch (widgetId) {
      case 'kpi-entities':
        return (
          <KPIWidget
            label="Total Entites"
            value={totalEntities.toLocaleString()}
            icon={
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
              </svg>
            }
            color="cyan"
            onClick={() => navigate('/entities')}
          />
        );

      case 'kpi-alerts':
        return (
          <KPIWidget
            label="Alertes actives"
            value={activeAlerts.toString()}
            icon={
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
              </svg>
            }
            color="amber"
            trend={alertsLast24h > 0 ? { direction: 'up', percentage: alertsLast24h } : undefined}
            onClick={() => navigate('/alerts')}
          />
        );

      case 'kpi-relationships':
        return (
          <KPIWidget
            label="Relations"
            value={totalRelationships.toLocaleString()}
            icon={
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5" />
              </svg>
            }
            color="purple"
            onClick={() => navigate('/graph')}
          />
        );

      case 'kpi-communities':
        return (
          <KPIWidget
            label="Communautes"
            value={(graphStats?.communities ?? 0).toString()}
            icon={
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6z" />
              </svg>
            }
            color="green"
            onClick={() => navigate('/graph')}
          />
        );

      case 'chart-activity':
        return (
          <ChartWidget
            title="Activite (24 dernieres heures)"
            type="area"
            data={activityData}
            xKey="hour"
            series={[
              { dataKey: 'entities', color: '#06b6d4', name: 'Entites' },
              { dataKey: 'alerts', color: '#ef4444', name: 'Alertes' },
              { dataKey: 'analysis', color: '#a855f7', name: 'Analyses' },
            ]}
            height={240}
          />
        );

      case 'chart-entity-distribution':
        return (
          <div className="glass-panel p-4 h-full flex flex-col">
            <h3 className="text-sm font-semibold text-gray-300 mb-4">Distribution des entites</h3>
            {entityTypePieData.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={entityTypePieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={3}
                      dataKey="value"
                    >
                      {entityTypePieData.map((entry, index) => (
                        <Cell key={index} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        background: '#1a2744',
                        border: '1px solid #2d4a6f',
                        borderRadius: '8px',
                        fontSize: '12px',
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <div className="mt-2 flex flex-wrap gap-2">
                  {entityTypePieData.slice(0, 5).map((item) => (
                    <div key={item.name} className="flex items-center gap-1.5 text-xs text-gray-400">
                      <div className="w-2.5 h-2.5 rounded-full" style={{ background: item.color }} />
                      <span className="capitalize">{item.name}</span>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="flex items-center justify-center flex-1 text-gray-500 text-sm">
                Aucune donnee
              </div>
            )}
          </div>
        );

      case 'chart-severity':
        return (
          <div className="glass-panel p-4 h-full flex flex-col">
            <h3 className="text-sm font-semibold text-gray-300 mb-4">Severite des alertes</h3>
            {alertSeverityData.length > 0 ? (
              <ResponsiveContainer width="100%" height={150}>
                <BarChart data={alertSeverityData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e3a5f" />
                  <XAxis type="number" tick={{ fill: '#64748b', fontSize: 10 }} />
                  <YAxis
                    dataKey="severity"
                    type="category"
                    tick={{ fill: '#64748b', fontSize: 10 }}
                    width={60}
                  />
                  <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                    {alertSeverityData.map((entry, index) => (
                      <Cell key={index} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center flex-1 text-gray-500 text-sm">
                Aucune donnee
              </div>
            )}
          </div>
        );

      case 'risk-gauge':
        return (
          <div className="glass-panel p-4 h-full flex flex-col items-center justify-center">
            <h3 className="text-sm font-semibold text-gray-300 mb-2">Score de risque moyen</h3>
            <RiskGauge score={0.45} size={130} label="Global" />
          </div>
        );

      case 'alert-list':
        return <AlertListWidget />;

      case 'mini-graph':
        return <MiniGraphWidget />;

      case 'activity-feed':
        return <ActivityFeedWidget />;

      case 'risk-overview':
        return <RiskOverviewWidget />;

      case 'map':
        return <MapWidget />;

      default:
        return (
          <div className="glass-panel p-4 h-full flex items-center justify-center text-gray-500 text-sm">
            Widget inconnu: {widgetId}
          </div>
        );
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Page Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-gray-100">Intelligence Dashboard</h1>
            {/* Live pulsing dot */}
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-surface-secondary border border-border-subtle">
              <span className="relative flex h-2.5 w-2.5">
                {isConnected && (
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                )}
                <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${isConnected ? 'bg-green-500' : 'bg-gray-500'}`} />
              </span>
              <span className={`text-xs font-medium ${isConnected ? 'text-green-400' : 'text-gray-500'}`}>
                {isConnected ? 'Live' : 'Offline'}
              </span>
            </div>
          </div>
          <p className="text-sm text-gray-500 mt-1">Xawio Intelligence Platform overview</p>
        </div>

        {/* Live event stats */}
        <div className="flex items-center gap-4">
          {liveEventCount > 0 && (
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-surface-secondary border border-border-subtle">
              <svg className="w-4 h-4 text-cyber-cyan" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" />
              </svg>
              <span className="text-sm font-semibold text-gray-200">{liveEventCount}</span>
              <span className="text-xs text-gray-500">events</span>
            </div>
          )}
          {lastEventTime && (
            <div className="text-xs text-gray-500">
              Dernier: {lastEventTime.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
            </div>
          )}
        </div>
      </div>

      {/* Widget Grid */}
      <WidgetGrid
        defaultLayout={DEFAULT_LAYOUT}
        renderWidget={renderWidget}
        storageKey="main-dashboard"
      />
    </div>
  );
}

// ── Sub-components (kept for backward compatibility) ──

export function SeverityDot({ severity }: { severity: AlertSeverity }) {
  const colors: Record<AlertSeverity, string> = {
    critical: 'bg-red-500',
    high: 'bg-orange-500',
    medium: 'bg-amber-500',
    low: 'bg-blue-500',
    info: 'bg-cyan-500',
  };

  return (
    <div className="flex-shrink-0">
      <div className={`w-2.5 h-2.5 rounded-full ${colors[severity]}`} />
    </div>
  );
}
